// =============================================================================
// hooks/crm/useLeads.ts — Lista paginada de leads com filtros.
//
// TanStack Query — nunca useEffect+fetch.
// Query key inclui filtros para que cada combinação seja cacheada separadamente.
// =============================================================================

import { useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api';

import { buildLeadFiltersQuery } from './leadFilters';
import type { LeadFilters, LeadListResponse } from './types';

export const LEADS_QUERY_KEY = (filters: LeadFilters) => ['leads', 'list', filters] as const;

// ─── Fetch ────────────────────────────────────────────────────────────────────

async function fetchLeads(filters: LeadFilters): Promise<LeadListResponse> {
  const qs = buildLeadFiltersQuery(filters, { includePaging: true });

  return api.get<LeadListResponse>(`/api/leads${qs ? `?${qs}` : ''}`);
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Lista paginada de leads com filtros.
 * Query key inclui filtros: cada combinação tem cache separado.
 */
export function useLeads(filters: LeadFilters = {}): {
  data: LeadListResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: LEADS_QUERY_KEY(filters),
    queryFn: () => fetchLeads(filters),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  return {
    data,
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
  };
}
